import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  BookOpen,
  FileText,
  Newspaper,
  HelpCircle,
  ArrowRight,
  Sparkles,
  Search,
  TrendingUp,
  Users,
  Zap,
  BookMarked,
  LifeBuoy,
  Globe,
  Shield,
  Star,
  Code,
  Beaker,
  GraduationCap,
  CheckCircle2,
} from 'lucide-react';
import landingService from '../../../services/landingService';
import LandingNavbar from '../LandingNavbar';
import LandingFooter from '../LandingFooter';
import './ResourcesPage.css';

/**
 * ResourcesPage — Central hub linking to Documentation, Research Guides, Blog, Help Center
 * Premium design with hero, resource cards, featured content, and CTA
 */

const resourceSections = [
  {
    id: 'documentation',
    icon: BookOpen,
    title: 'Documentation',
    subtitle: 'Learn how to use the platform',
    description:
      'Comprehensive guides covering everything from account setup to advanced API integration. Step-by-step walkthroughs, searchable knowledge base, and developer resources.',
    to: '/docs',
    color: '#00C853',
    highlights: ['Quick Start Guide', 'Platform Guides', 'API Reference', 'WebSocket Events'],
    stat: { value: '60+', label: 'Articles' },
  },
  {
    id: 'guides',
    icon: BookMarked,
    title: 'Research Guides',
    subtitle: 'Best practices for researchers',
    description:
      'Expert-curated guides for every stage of the research lifecycle. Literature reviews, methodology frameworks, collaboration strategies, and writing best practices.',
    to: '/guides',
    color: '#7C4DFF',
    highlights: ['Literature Reviews', 'Team Collaboration', 'Scientific Writing', 'Data Analysis'],
    stat: { value: '50+', label: 'Guides' },
  },
  {
    id: 'blog',
    icon: Newspaper,
    title: 'Blog',
    subtitle: 'Latest updates and insights',
    description:
      'Product announcements, research insights, engineering deep dives, and community stories. Stay informed on everything happening at ResearchPro.',
    to: '/blog',
    color: '#FF8F00',
    highlights: ['Product Updates', 'Research Insights', 'Engineering', 'Community Stories'],
    stat: { value: 'Weekly', label: 'New Posts' },
  },
  {
    id: 'help',
    icon: LifeBuoy,
    title: 'Help Center',
    subtitle: 'Get support when you need it',
    description:
      'Search our knowledge base, browse FAQ, check system status, or reach out to our support team directly. We\'re here to help you succeed.',
    to: '/help',
    color: '#2979FF',
    highlights: ['Knowledge Base', 'FAQ', 'Live Chat', 'System Status'],
    stat: { value: '24h', label: 'Response Time' },
  },
];

const popularArticles = [
  {
    icon: Beaker,
    title: 'Setting Up Your First Research Lab',
    category: 'Documentation',
    to: '/docs',
    color: '#00C853',
  },
  {
    icon: Search,
    title: 'Mastering AI-Powered Paper Search',
    category: 'Guide',
    to: '/guides',
    color: '#7C4DFF',
  },
  {
    icon: Shield,
    title: 'End-to-End Encryption Architecture',
    category: 'Blog',
    to: '/blog',
    color: '#FF8F00',
  },
  {
    icon: Users,
    title: 'Building High-Performance Research Teams',
    category: 'Guide',
    to: '/guides',
    color: '#7C4DFF',
  },
  {
    icon: GraduationCap,
    title: 'Courses & Workshops: Getting Started',
    category: 'Documentation',
    to: '/docs',
    color: '#00C853',
  },
  {
    icon: Code,
    title: 'Scaling Real-Time Chat Infrastructure',
    category: 'Blog',
    to: '/blog',
    color: '#FF8F00',
  },
];

const ResourcesPage = () => {
  const [visibleSections, setVisibleSections] = useState(new Set());
  const [statusText, setStatusText] = useState('All systems operational');
  const [statusOk, setStatusOk] = useState(true);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const fetchStatus = async () => {
      const result = await landingService.getSystemStatus();
      if (result.success) {
        const overall = result.overallStatus || 'operational';
        setStatusOk(overall === 'operational');
        setStatusText(
          overall === 'operational' ? 'All systems operational'
            : overall === 'degraded' ? 'Partial degradation'
            : 'Service disruption'
        );
      }
    };
    fetchStatus();
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisibleSections((prev) => new Set([...prev, entry.target.id]));
          }
        });
      },
      { threshold: 0.12, rootMargin: '-60px 0px -30% 0px' }
    );

    document.querySelectorAll('.rsp-animate').forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  const isVisible = (id) => visibleSections.has(id);

  return (
    <div className="rsp-page">
      <LandingNavbar />

      {/* ── Hero ── */}
      <section id="rsp-hero" className={`rsp-hero rsp-animate ${isVisible('rsp-hero') ? 'rsp-visible' : ''}`}>
        <div className="rsp-hero-inner">
          <h1 className="rsp-hero-title">
            Everything you need,<br />
            <span className="rsp-hero-gradient">all in one place</span>
          </h1>
          <p className="rsp-hero-subtitle">
            Documentation, research guides, product updates, and support — curated
            to help you get the most out of ResearchPro.
          </p>
        </div>
      </section>

      {/* ── Resource Cards ── */}
      <section id="rsp-cards" className={`rsp-cards-section rsp-animate ${isVisible('rsp-cards') ? 'rsp-visible' : ''}`}>
        <div className="rsp-cards-grid">
          {resourceSections.map((section, index) => {
            const Icon = section.icon;
            return (
              <Link
                key={section.id}
                to={section.to}
                className="rsp-resource-card"
                style={{ '--card-color': section.color, '--card-delay': `${index * 80}ms` }}
              >
                <div className="rsp-card-header">
                  <div className="rsp-card-icon">
                    <Icon size={22} />
                  </div>
                  <div className="rsp-card-stat">
                    <span className="rsp-card-stat-value">{section.stat.value}</span>
                    <span className="rsp-card-stat-label">{section.stat.label}</span>
                  </div>
                </div>

                <h2 className="rsp-card-title">{section.title}</h2>
                <p className="rsp-card-subtitle">{section.subtitle}</p>
                <p className="rsp-card-desc">{section.description}</p>

                <div className="rsp-card-highlights">
                  {section.highlights.map((h, i) => (
                    <span key={i} className="rsp-card-highlight">
                      <CheckCircle2 size={12} />
                      {h}
                    </span>
                  ))}
                </div>

                <span className="rsp-card-link">
                  Explore {section.title}
                  <ArrowRight size={15} />
                </span>
              </Link>
            );
          })}
        </div>
      </section>

      {/* ── Popular Articles ── */}
      <section id="rsp-popular" className={`rsp-popular-section rsp-animate ${isVisible('rsp-popular') ? 'rsp-visible' : ''}`}>
        <div className="rsp-popular-inner">
          <div className="rsp-popular-header">
            <h2 className="rsp-popular-title">Popular articles</h2>
            <p className="rsp-popular-subtitle">
              The most-read content across our resource library.
            </p>
          </div>

          <div className="rsp-popular-grid">
            {popularArticles.map((article, i) => {
              const Icon = article.icon;
              return (
                <Link
                  key={i}
                  to={article.to}
                  className="rsp-popular-item"
                  style={{ '--item-color': article.color }}
                >
                  <div className="rsp-popular-item-icon">
                    <Icon size={18} />
                  </div>
                  <div className="rsp-popular-item-text">
                    <span className="rsp-popular-item-title">{article.title}</span>
                    <span className="rsp-popular-item-cat">{article.category}</span>
                  </div>
                  <ArrowRight size={15} className="rsp-popular-item-arrow" />
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── Quick Links Bar ── */}
      <section id="rsp-quick" className={`rsp-quick-section rsp-animate ${isVisible('rsp-quick') ? 'rsp-visible' : ''}`}>
        <div className="rsp-quick-inner">
          <div className="rsp-quick-cards">
            <div className="rsp-quick-card">
              <Globe size={20} />
              <div>
                <span className="rsp-quick-card-title">Platform Status</span>
                <span className="rsp-quick-card-desc">{statusText}</span>
              </div>
              <span className={`rsp-quick-card-dot ${!statusOk ? 'rsp-quick-card-dot--warn' : ''}`} />
            </div>
            <div className="rsp-quick-card">
              <Star size={20} />
              <div>
                <span className="rsp-quick-card-title">What's New</span>
                <span className="rsp-quick-card-desc">Latest feature releases</span>
              </div>
              <ArrowRight size={15} />
            </div>
            <div className="rsp-quick-card">
              <TrendingUp size={20} />
              <div>
                <span className="rsp-quick-card-title">Roadmap</span>
                <span className="rsp-quick-card-desc">See what's coming next</span>
              </div>
              <ArrowRight size={15} />
            </div>
          </div>
        </div>
      </section>

      {/* ── Bottom CTA ── */}
      <section id="rsp-cta" className={`rsp-cta rsp-animate ${isVisible('rsp-cta') ? 'rsp-visible' : ''}`}>
        <div className="rsp-cta-inner">
          <span className="rsp-cta-badge">
            <Zap size={13} />
            Get Started
          </span>
          <h2 className="rsp-cta-title">Can't find what you need?</h2>
          <p className="rsp-cta-subtitle">
            Our support team is here to help. Reach out through the Help Center
            or join a community for peer support.
          </p>
          <div className="rsp-cta-actions">
            <Link to="/help" className="rsp-btn rsp-btn--primary">
              Visit Help Center
              <ArrowRight size={18} />
            </Link>
            <Link to="/signup" className="rsp-btn rsp-btn--secondary">
              Create Free Account
            </Link>
          </div>
        </div>
      </section>

      <LandingFooter />
    </div>
  );
};

export default ResourcesPage;
